import { useEffect, useRef } from "react";
import { Phase, TradeToEarnLeaderboardDto, TradeToEarnCampaignEpoch } from "@cede/types";
import { QueryKeys } from "@cede/utils";
import { useLeaderboard } from "@/hooks/campaign/useLeaderboard";
import { useHubUser } from "@/hooks/user/useHubUser";
import { hasUserVolumeChanged } from "@/hooks/volumeTracker/utils";
import { getUserPhaseVolume } from "@/utils/api";
import { useQuery } from "@tanstack/react-query";
import Decimal from "decimal.js";

/**
 * This hook is used to get the user phase volume (only for PVP campaigns)
 */
export const useUserPhaseVolume = (campaignName: string, epoch?: TradeToEarnCampaignEpoch, phase?: Phase) => {
  const { auth, hubUser } = useHubUser();

  /**
   * @remarks
   * We track the previous leaderboard to avoid unnecessary refetches
   */
  const previousLeaderboard = useRef<TradeToEarnLeaderboardDto | undefined>(undefined);
  // Only call useLeaderboard when we have both epoch and phase (i.e., when this hook should be active)
  const { leaderboard } = useLeaderboard(campaignName, epoch && phase ? epoch : undefined);

  const { data: userPhaseVolume, refetch: refetchUserPhaseVolume } = useQuery({
    queryKey: [QueryKeys.PHASE_VOLUME, campaignName, epoch?.id, phase?.id],
    queryFn: () => {
      if (!epoch || !phase) {
        return Promise.resolve(new Decimal(0));
      }
      return getUserPhaseVolume(campaignName, epoch.id || 0 || 0, phase.id, auth);
    },
    enabled: !!campaignName && !!epoch && !!phase,
  });

  useEffect(() => {
    if (leaderboard && previousLeaderboard.current && hubUser) {
      if (hasUserVolumeChanged(hubUser.id, previousLeaderboard.current, leaderboard)) {
        refetchUserPhaseVolume();
      }
    }
    previousLeaderboard.current = leaderboard;
  }, [leaderboard, hubUser]);

  return { userPhaseVolume: userPhaseVolume || new Decimal(0), refetchUserPhaseVolume };
};
